import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import LoadingModal from 'src/components/navigation/LoadingModal';
import { CURRENCIES } from 'src/features/payStations/types';
import { useListPaymentDetailsStatistics } from 'src/features/statistics/api/useListPaymentDetailsStatistics';
import { useListPaymentsByTypeStatistics } from 'src/features/statistics/api/useListPaymentsByTypeStatistics';
import {
    PAYMENT_EXPORT_MESSAGES_MAP,
    PAYMENT_EXPORT_PROPS,
    PAYMENT_TYPE,
    PAYMENT_TYPE_MAP,
    type StatisticPaymentDetails,
} from 'src/features/statistics/types';
import { statisticsMessages } from 'src/features/statistics/utils';
import { Button } from 'src/registry/ui/Button';
import { formatTime } from 'src/utils/formatTime';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import { generalMessages } from 'src/utils/messages';

type Props = {
    parkingId: string;
    dateTime: {
        beforeDate: Date;
        afterDate: Date;
    };
    querySortingOptions: { ordering: string | undefined };
    queryFiltersOptions: { [p: string]: any } | any[];
    querySearchOptions: { [p: string]: string };
    disabled: boolean;
};

type ExtraPaymentData = {
    amount: number;
    name: string;
    currency: CURRENCIES;
}[][];

const LIMIT = 100;

export default function PaymentExport({
    parkingId,
    dateTime,
    querySortingOptions,
    queryFiltersOptions,
    querySearchOptions,
    disabled,
}: Props) {
    const [offset, setOffset] = React.useState<null | number>(null);
    const [exportJSON, setExportJSON] = React.useState<StatisticPaymentDetails[]>([]);
    const { formatMessage } = useIntl();

    const enabled = React.useMemo(() => typeof offset === 'number', [offset]);

    const { data: exportDetails } = useListPaymentDetailsStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        offset,
        limit: LIMIT,
        config: { enabled },
    });

    const toDate = React.useMemo(() => {
        const clone = new Date(dateTime.beforeDate);
        clone.setDate(clone.getDate() + 1);
        return clone.toISOString();
    }, [dateTime]);

    const { data: paymentsByType } = useListPaymentsByTypeStatistics({
        parkingId,
        to_date: toDate,
        from_date: dateTime.afterDate.toDateString(),
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        config: { enabled },
    });

    const formattedPaymentsByType = React.useMemo(() => {
        if (!paymentsByType) return [];
        const result = paymentsByType.results.map((payment, index) => {
            return payment.details.map((details) => {
                const message = PAYMENT_TYPE_MAP[details.name as PAYMENT_TYPE];

                const paymentTypeName = message
                    ? formatMessage(message)
                    : index === 0
                      ? formatMessage(statisticsMessages.total)
                      : details.name;

                return {
                    name: paymentTypeName,
                    amount: (details.price_total / 100).toFixed(2),
                    currency: details.currency,
                };
            });
        });
        const total = result.shift();
        result.push(total || []);
        const formattedResult = result.flat(1);
        return Object.values(CURRENCIES).map((currency) =>
            formattedResult.filter((paymentMethod) => paymentMethod.currency === currency),
        );
    }, [paymentsByType]);

    const onExportEnd = React.useCallback(() => {
        setExportJSON([]);
        setOffset(null);
    }, []);

    const json2Csv = (jsonData: StatisticPaymentDetails[], extraJsonData: ExtraPaymentData) => {
        const keys = Object.values(PAYMENT_EXPORT_PROPS) as PAYMENT_EXPORT_PROPS[];
        const csvRows = [];

        csvRows.push(keys.map((key) => formatMessage(PAYMENT_EXPORT_MESSAGES_MAP[key])).join(','));
        jsonData.forEach((row) => {
            const values = keys
                .filter((key) => key !== PAYMENT_EXPORT_PROPS.TIME)
                .map((key) => {
                    const rawValue = (row as Record<string, any>)[key] as string | null | undefined;
                    const value = rawValue === null || rawValue === undefined ? '' : rawValue.toString();
                    if (key === PAYMENT_EXPORT_PROPS.PAYMENT_DATETIME) {
                        const datetime = new Date(value);
                        const formattedDate = new Date(datetime.toString()).toLocaleDateString('bg-BG');
                        const formattedTime = formatTime(datetime);
                        return `${formattedDate},${formattedTime}`;
                    }
                    switch (key) {
                        case PAYMENT_EXPORT_PROPS.PRICE_VAT_ONLY:
                            return (Number(value) / 100).toFixed(2);
                        case PAYMENT_EXPORT_PROPS.PRICE_TOTAL:
                            return (Number(value) / 100).toFixed(2);
                        case PAYMENT_EXPORT_PROPS.PRICE_NO_VAT:
                            return (Number(value) / 100).toFixed(2);
                        case PAYMENT_EXPORT_PROPS.FISCAL_NOTE_NUMBER:
                            return isNaN(Number(value)) ? value : `'${value}'`;
                    }

                    if (value.includes(',')) return `"${value}"`;

                    return value;
                });
            csvRows.push(values.join(','));
        });
        csvRows.push([].join(','));
        extraJsonData.map((data) => {
            data.map((currencyData) => {
                csvRows.push([currencyData.name, currencyData.amount, currencyData.currency].join(','));
            });
            csvRows.push([].join(','));
        });

        return csvRows.join('\n');
    };

    const generateCSV = (data: StatisticPaymentDetails[], title: string, extraData: ExtraPaymentData) => {
        const csv = json2Csv(data, extraData);
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const { formattedDate } = getFormattedDateTime(new Date().toISOString());
        link.href = url;
        link.download = `${title}_${formattedDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    React.useEffect(() => {
        if (!enabled || !exportDetails || !exportDetails.results || !formattedPaymentsByType) return;
        if (exportDetails.count > (offset || 0) + LIMIT) {
            setExportJSON((prevState) => prevState.concat(exportDetails.results));
            return setOffset((prevState) => (prevState || 0) + LIMIT);
        }
        generateCSV(exportJSON.concat(exportDetails.results), 'Payments', formattedPaymentsByType);
        onExportEnd();
    }, [setExportJSON, offset, generateCSV, exportDetails, formattedPaymentsByType]);

    return (
        <>
            <LoadingModal
                isLoading={enabled}
                title={<FormattedMessage {...statisticsMessages.exportingPayments} />}
                onCancel={onExportEnd}
            />
            <Button onClick={() => setOffset(0)} disabled={disabled}>
                <FormattedMessage {...generalMessages.exportCSV} />
            </Button>
        </>
    );
}
